import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import { useNavigate } from "@tanstack/react-router"
import type { FC } from "react"
import { useForm } from "react-hook-form"

import { TRANSFER_TYPE_OPTIONS, TransferType } from "../../constants"
import { type TransferLaunchFormValues, transferLaunchFormSchema } from "../../schemas"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { useGetWarehouseOptions } from "@/hooks/useGetWarehouseOptions"

interface TransferLaunchFormDialogProps {
  onClose: () => void
}

export const TransferLaunchFormDialog: FC<TransferLaunchFormDialogProps> = ({ onClose }) => {
  const navigate = useNavigate()
  const { warehouseOptions } = useGetWarehouseOptions({ isActive: true })

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<TransferLaunchFormValues>({
    resolver: zodResolver(transferLaunchFormSchema),
    mode: "onChange",
    defaultValues: {
      type: TransferType.IntraWarehouse,
      sourceWarehouseAreaId: "",
    },
  })

  const onSubmit = (formValues: TransferLaunchFormValues) => {
    navigate({ to: "/app/inventory/transfers/new", search: formValues })
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { minWidth: 520 } }}
      data-testid='transfer.launch-form-dialog'
    >
      <DialogHeader title='New Transfer' onClose={onClose} />
      <DialogContent className='flex flex-col gap-y-6 p-10'>
        <FormSelect
          control={control}
          name='type'
          label='Transfer Type'
          required
          options={TRANSFER_TYPE_OPTIONS}
          data-testid='transfer.launch-form-dialog.transfer-type-field'
        />
        <FormSelect
          control={control}
          name='sourceWarehouseAreaId'
          label='Source Warehouse'
          required
          options={warehouseOptions}
          data-testid='transfer.launch-form-dialog.source-warehouse-field'
        />
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={onClose} data-testid='transfer.launch-form-dialog.cancel-button'>
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!isValid}
          onClick={handleSubmit(onSubmit)}
          data-testid='transfer.launch-form-dialog.continue-button'
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  )
}
